import React from "react";
import { t } from "ttag";

import type { ServerCmsTypesFilterFacet as FilterFacet } from "@reactivated";

import { notEmpty } from "@thelabnyc/thelabui/src/utils/functional";

import { Clickable } from "../Clickables";
import { Svg } from "../Svg";
import type { SelectedFilters } from "./filterProducts";

import styles from "./FilterChips.module.scss";

interface FilterChipsProps {
    filterFacets: FilterFacet[];
    selectedFilters: SelectedFilters;
    setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedFilters>>;
    clearAllFilters: () => void;
}

export const FilterChips = ({
    filterFacets,
    selectedFilters,
    setSelectedFilters,
    clearAllFilters,
}: FilterChipsProps) => {
    const removeFilter = (facetSlug: string, valueSlug: string) => {
        setSelectedFilters((prev) => {
            const current = prev[facetSlug];
            /* v8 ignore next -- @preserve */
            if (!current) return prev;
            const next = current.filter((v) => v !== valueSlug);
            const updated = { ...prev };
            if (next.length === 0) {
                delete updated[facetSlug];
            } else {
                updated[facetSlug] = next;
            }
            return updated;
        });
    };

    const activeChips = Object.entries(selectedFilters).flatMap(
        ([facetSlug, valueSlugs]) => {
            const facet = filterFacets.find((f) => f.slug === facetSlug);
            /** v8 ignore next -- @preserve */
            if (!facet) return [];
            return valueSlugs.map((valueSlug) => {
                const value = facet.values.find((v) => v.slug === valueSlug);
                /* v8 ignore next -- @preserve */
                if (!value) return;
                return {
                    facetSlug,
                    valueSlug,
                    label: `${facet.name}: ${value.display_value}`,
                };
            });
        },
    );

    return (
        <div className={styles.filterChipsWrapper}>
            <ul className={styles.chipList} aria-label={t`Active filters`}>
                {activeChips.filter(notEmpty).map((chip) => (
                    <li
                        key={`${chip.facetSlug}-${chip.valueSlug}`}
                        className={styles.chip}
                    >
                        <span id={`chip-${chip.facetSlug}-${chip.valueSlug}`}>
                            {chip.label}
                        </span>
                        <Clickable
                            className={styles.chipClose}
                            aria-label={t`Remove filter`}
                            aria-describedby={`chip-${chip.facetSlug}-${chip.valueSlug}`}
                            onPress={() =>
                                removeFilter(chip.facetSlug, chip.valueSlug)
                            }
                        >
                            <Svg
                                name="x"
                                className={styles.chipX}
                                aria-hidden="true"
                            />
                        </Clickable>
                    </li>
                ))}
                <li>
                    <Clickable
                        onPress={clearAllFilters}
                        className={styles.chipsClearAll}
                    >
                        {t`Clear All`}
                    </Clickable>
                </li>
            </ul>
        </div>
    );
};
